import { ref, Ref } from 'vue';
import { EnumApiCaller, EnumOption } from '../utils/EnumApiCaller';

/**
 * Session-cached enum values composable
 * Loads enum options once per browser session and caches them in memory
 *
 * Usage:
 * ```typescript
 * import { useEnums } from '@/composables/useEnums';
 *
 * const { orderStatusOptions, loadAllEnums } = useEnums();
 *
 * // In onMounted
 * await loadAllEnums();
 *
 * // Use in v-select
 * <v-select :items="orderStatusOptions" item-title="value" item-value="value" />
 * ```
 */

// Global state - persists across component instances for session duration
const orderStatusOptions: Ref<EnumOption[]> = ref([]);
const paymentStatusOptions: Ref<EnumOption[]> = ref([]);
const refundStatusOptions: Ref<EnumOption[]> = ref([]);
const paymentTypeOptions: Ref<EnumOption[]> = ref([]);
const refundTypeOptions: Ref<EnumOption[]> = ref([]);
const balanceTypeOptions: Ref<EnumOption[]> = ref([]);

// Loading state
const isLoading: Ref<boolean> = ref(false);
const isLoaded: Ref<boolean> = ref(false);
const loadError: Ref<string> = ref('');

/**
 * Composable function for accessing enum options
 * Returns reactive refs that are shared across all component instances
 */
export function useEnums() {
    /**
     * Load all enum options from API
     * Only loads once per session - subsequent calls return immediately
     * @returns Promise that resolves when all enums are loaded
     */
    const loadAllEnums = async (): Promise<void> => {
        // Skip if already loaded or currently loading
        if (isLoaded.value || isLoading.value) {
            return;
        }

        isLoading.value = true;
        loadError.value = '';

        try {
            // Load all enums in parallel for better performance
            const [
                orderStatus,
                paymentStatus,
                refundStatus,
                paymentType,
                refundType,
                balanceType
            ] = await Promise.all([
                EnumApiCaller.getOrderStatusOptions(),
                EnumApiCaller.getPaymentStatusOptions(),
                EnumApiCaller.getRefundStatusOptions(),
                EnumApiCaller.getPaymentTypeOptions(),
                EnumApiCaller.getRefundTypeOptions(),
                EnumApiCaller.getBalanceTypeOptions()
            ]);

            // Update reactive refs
            orderStatusOptions.value = orderStatus;
            paymentStatusOptions.value = paymentStatus;
            refundStatusOptions.value = refundStatus;
            paymentTypeOptions.value = paymentType;
            refundTypeOptions.value = refundType;
            balanceTypeOptions.value = balanceType;

            isLoaded.value = true;

            console.log('✅ Enums loaded successfully:', {
                orderStatus: orderStatus.length,
                paymentStatus: paymentStatus.length,
                refundStatus: refundStatus.length,
                paymentType: paymentType.length,
                refundType: refundType.length,
                balanceType: balanceType.length
            });
        } catch (error) {
            loadError.value = error instanceof Error ? error.message : 'Failed to load enum options';
            console.error('❌ Failed to load enums:', error);

            // Set empty arrays as fallback
            orderStatusOptions.value = [];
            paymentStatusOptions.value = [];
            refundStatusOptions.value = [];
            paymentTypeOptions.value = [];
            refundTypeOptions.value = [];
            balanceTypeOptions.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Force reload all enum options
     * Use this if you need to refresh enum data
     */
    const reloadEnums = async (): Promise<void> => {
        isLoaded.value = false;
        await loadAllEnums();
    };

    /**
     * Clear all cached enum data
     * Useful for testing or when user logs out
     */
    const clearEnums = (): void => {
        orderStatusOptions.value = [];
        paymentStatusOptions.value = [];
        refundStatusOptions.value = [];
        paymentTypeOptions.value = [];
        refundTypeOptions.value = [];
        balanceTypeOptions.value = [];
        isLoaded.value = false;
        isLoading.value = false;
        loadError.value = '';
    };

    return {
        // Enum options (reactive refs)
        orderStatusOptions,
        paymentStatusOptions,
        refundStatusOptions,
        paymentTypeOptions,
        refundTypeOptions,
        balanceTypeOptions,

        // Loading state
        isLoading,
        isLoaded,
        loadError,

        // Methods
        loadAllEnums,
        reloadEnums,
        clearEnums
    };
}
